import { useEffect, useRef, useState } from "react";
import { playTickSound, playTimeUpSound } from "@/utils/sounds";

/**
 * Live countdown display driven by a server-side `timerEndAt` timestamp.
 * Visible to all players when the host starts a round timer.
 * Plays tick sounds in the last 5 seconds and a "time's up" chime at 0.
 *
 * Props:
 *  - endAt (string | null): ISO timestamp when the timer ends
 *  - duration (number | null): total seconds (for the progress bar)
 *  - theme
 */
const RoundTimer = ({ endAt, duration, theme }) => {
  const [remainingMs, setRemainingMs] = useState(() =>
    endAt ? Math.max(0, new Date(endAt).getTime() - Date.now()) : 0
  );
  const lastTickSecondRef = useRef(null);
  const timeUpFiredRef = useRef(false);

  useEffect(() => {
    if (!endAt) return undefined;

    // Reset the tick/timeup guards for each new timer instance
    lastTickSecondRef.current = null;
    timeUpFiredRef.current = false;

    const end = new Date(endAt).getTime();

    const tick = () => {
      const remain = Math.max(0, end - Date.now());
      setRemainingMs(remain);

      const secLeft = Math.ceil(remain / 1000);

      // Tick sound in final 5 seconds (once per second)
      if (secLeft > 0 && secLeft <= 5 && lastTickSecondRef.current !== secLeft) {
        lastTickSecondRef.current = secLeft;
        try {
          playTickSound();
        } catch (_) {
          /* audio can throw before first user gesture — ignore */
        }
      }

      // Time's-up sound (once)
      if (remain <= 0 && !timeUpFiredRef.current) {
        timeUpFiredRef.current = true;
        try {
          playTimeUpSound();
        } catch (_) {
          /* ignore */
        }
      }
    };

    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [endAt]);

  if (!endAt) return null;

  const remainingSec = Math.ceil(remainingMs / 1000);
  const total = duration || 1;
  const percent = Math.max(0, Math.min(100, (remainingMs / (total * 1000)) * 100));
  const isCritical = remainingSec <= 5 && remainingSec > 0;
  const isDone = remainingMs <= 0;

  const barColor = isDone
    ? "#FF0055"
    : isCritical
    ? "#FF0055"
    : theme?.accentColor || "#00F0FF";

  return (
    <div className="w-full max-w-md mx-auto mb-4" data-testid="round-timer">
      <div className="flex items-center justify-between mb-2">
        <span
          className="text-xs tracking-[0.3em] font-semibold"
          style={{ color: theme?.accentColor || "#00F0FF" }}
        >
          TIME LEFT
        </span>
        <span
          className={`tabular-nums text-3xl sm:text-4xl transition-transform ${
            isCritical ? "animate-pulse scale-110" : ""
          }`}
          style={{
            fontFamily: "Anton, sans-serif",
            color: isDone || isCritical ? "#FF0055" : "#FFFFFF",
            textShadow: isCritical ? "0 0 12px #FF0055" : "none",
          }}
          data-testid="timer-seconds-left"
        >
          {isDone ? "0" : remainingSec}s
        </span>
      </div>
      <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-[width] duration-100 ease-linear"
          style={{
            width: `${percent}%`,
            background: barColor,
            boxShadow: isCritical ? `0 0 10px ${barColor}` : "none",
          }}
        />
      </div>
    </div>
  );
};

export default RoundTimer;
